import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { PlayIcon, StopCircleIcon } from 'lucide-react';
export function TourManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'apply' | 'tracking' | 'history' | 'startTrip'>('applications');
  const [currentStep, setCurrentStep] = useState(0);
  // Apply Permit Form State
  const [applyForm, setApplyForm] = useState({
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    tourType: '',
    duration: '',
    groupSize: '',
    forestZone: '',
    purpose: '',
    guideSelection: '',
    vesselType: ''
  });
  // Start Trip Form State
  const [startTripForm, setStartTripForm] = useState({
    permitId: '',
    startLocation: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5)
  });
  // Data states
  const [applications, setApplications] = useState([{
    permitId: 'TOUR-2024-001',
    contactName: 'John Doe',
    tourType: 'Eco Tour',
    appliedDate: '2024-03-28',
    duration: '3 days',
    guideName: 'Mostafa Kamal',
    status: 'pending' as const
  }, {
    permitId: 'TOUR-2024-002',
    contactName: 'Jane Smith',
    tourType: 'Educational Tour',
    appliedDate: '2024-03-27',
    duration: '5 days',
    guideName: 'Rafiq Ahmed',
    status: 'approved' as const
  }, {
    permitId: 'TOUR-2024-003',
    contactName: 'Robert Johnson',
    tourType: 'Research Tour',
    appliedDate: '2024-03-26',
    duration: '7 days',
    guideName: 'Selina Akter',
    status: 'approved' as const
  }]);
  const [activeTrips, setActiveTrips] = useState([{
    contactName: 'Jane Smith',
    guideName: 'Rafiq Ahmed',
    vesselType: 'Launch',
    zone: 'Chandpai Range',
    entryTime: '2024-03-30 06:00',
    elapsed: '4h 30m'
  }, {
    contactName: 'Robert Johnson',
    guideName: 'Selina Akter',
    vesselType: 'Boat',
    zone: 'Sarankhola Range',
    entryTime: '2024-03-30 05:30',
    elapsed: '5h 00m'
  }]);
  const [tripHistory, setTripHistory] = useState([{
    tripId: 'TRIP-TOUR-001',
    contactName: 'Alice Williams',
    guideName: 'Habibur Rahman',
    vesselType: 'Ship',
    zone: 'Chandpai Range',
    entry: '2024-03-29 06:00',
    exit: '2024-03-29 18:30',
    duration: '12h 30m',
    status: 'approved' as const
  }]);
  const approvedPermits = applications.filter(p => p.status === 'approved');
  const selectedPermit = approvedPermits.find(p => p.permitId === startTripForm.permitId);
  const applicationColumns = [{
    key: 'permitId',
    label: 'Permit ID'
  }, {
    key: 'contactName',
    label: 'Contact Name'
  }, {
    key: 'tourType',
    label: 'Tour Type'
  }, {
    key: 'appliedDate',
    label: 'Applied Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'guideName',
    label: 'Guide'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2556" />
  }];
  const historyColumns = [{
    key: 'tripId',
    label: 'Trip ID'
  }, {
    key: 'contactName',
    label: 'Contact Name'
  }, {
    key: 'guideName',
    label: 'Guide'
  }, {
    key: 'vesselType',
    label: 'Vessel Type'
  }, {
    key: 'zone',
    label: 'Zone'
  }, {
    key: 'entry',
    label: 'Entry Time'
  }, {
    key: 'exit',
    label: 'Exit Time'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2557" />
  }];
  const filters = [{
    key: 'tourType',
    label: 'Tour Type',
    options: ['Eco Tour', 'Educational Tour', 'Research Tour']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Approved', 'Rejected']
  }];
  const wizardSteps = [{
    title: 'Contact Information',
    description: 'Enter contact details'
  }, {
    title: 'Tour Details',
    description: 'Enter tour information'
  }, {
    title: 'Guide & Vessel',
    description: 'Select guide and vessel type'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-2558">
      <div data-id="element-2559">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2560">Tour Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2561">
          Manage tour permits and track trips
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2562">
        <div className="flex gap-4" data-id="element-2563">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2564">
            Tour Applications
          </button>
          <button onClick={() => setActiveTab('apply')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'apply' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2565">
            Apply for Tour Permit
          </button>
          <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tracking' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2566">
            Trip Tracking ({activeTrips.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'history' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2567">
            Trip History
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'applications' && <DataTable columns={applicationColumns} data={applications} filters={filters} onDataChange={setApplications} searchPlaceholder="Search tour permits..." data-id="element-2568" />}

      {activeTab === 'apply' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!applyForm.contactName || !applyForm.tourType) {
        alert('Please fill in required fields.');
        return;
      }
      const newPermit = {
        permitId: `TOUR-2024-${String(applications.length + 1).padStart(3, '0')}`,
        contactName: applyForm.contactName,
        tourType: applyForm.tourType,
        appliedDate: new Date().toISOString().split('T')[0],
        duration: applyForm.duration ? `${applyForm.duration} days` : 'Not specified',
        guideName: applyForm.guideSelection || 'Not assigned',
        status: 'pending' as const
      };
      setApplications([newPermit, ...applications]);
      alert('Tour permit application submitted successfully!');
      setCurrentStep(0);
      setActiveTab('applications');
      // Reset form
      setApplyForm({
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        tourType: '',
        duration: '',
        groupSize: '',
        forestZone: '',
        purpose: '',
        guideSelection: '',
        vesselType: ''
      });
    }} data-id="element-2569">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2570">
              <div data-id="element-2571">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2572">
                  Contact Name *
                </label>
                <input type="text" required value={applyForm.contactName} onChange={e => setApplyForm({
            ...applyForm,
            contactName: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2573" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2574">
                <div data-id="element-2575">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2576">
                    Contact Phone *
                  </label>
                  <input type="tel" required value={applyForm.contactPhone} onChange={e => setApplyForm({
              ...applyForm,
              contactPhone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2577" />
                </div>
                <div data-id="element-2578">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2579">
                    Contact Email
                  </label>
                  <input type="email" value={applyForm.contactEmail} onChange={e => setApplyForm({
              ...applyForm,
              contactEmail: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2580" />
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-2581">
              <div data-id="element-2582">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2583">
                  Tour Type *
                </label>
                <select value={applyForm.tourType} onChange={e => setApplyForm({
            ...applyForm,
            tourType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2584">
                  <option value="" data-id="element-2585">Select Type</option>
                  <option value="Eco Tour" data-id="element-2586">Eco Tour</option>
                  <option value="Educational Tour" data-id="element-2587">Educational Tour</option>
                  <option value="Research Tour" data-id="element-2588">Research Tour</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2589">
                <div data-id="element-2590">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2591">
                    Requested Duration (Days) *
                  </label>
                  <input type="number" value={applyForm.duration} onChange={e => setApplyForm({
              ...applyForm,
              duration: e.target.value
            })} placeholder="e.g. 3" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2592" />
                </div>
                <div data-id="element-2593">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2594">
                    Group Size *
                  </label>
                  <input type="number" value={applyForm.groupSize} onChange={e => setApplyForm({
              ...applyForm,
              groupSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2595" />
                </div>
              </div>
              <div data-id="element-2596">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2597">
                  Forest Zone/Area *
                </label>
                <select value={applyForm.forestZone} onChange={e => setApplyForm({
            ...applyForm,
            forestZone: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2598">
                  <option value="" data-id="element-2599">Select Zone</option>
                  <option value="Chandpai Range" data-id="element-2600">Chandpai Range</option>
                  <option value="Sarankhola Range" data-id="element-2601">Sarankhola Range</option>
                  <option value="Khulna Range" data-id="element-2602">Khulna Range</option>
                  <option value="Satkhira Range" data-id="element-2603">Satkhira Range</option>
                </select>
              </div>
              <div data-id="element-2604">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2605">
                  Purpose
                </label>
                <textarea rows={3} value={applyForm.purpose} onChange={e => setApplyForm({
            ...applyForm,
            purpose: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2606"></textarea>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-2607">
              <div data-id="element-2608">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2609">
                  Guide Selection *
                </label>
                <select value={applyForm.guideSelection} onChange={e => setApplyForm({
            ...applyForm,
            guideSelection: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2610">
                  <option value="" data-id="element-2611">Select Guide</option>
                  <option value="Mostafa Kamal" data-id="element-2612">Mostafa Kamal (GD-001)</option>
                  <option value="Rafiq Ahmed" data-id="element-2613">Rafiq Ahmed (GD-002)</option>
                  <option value="Selina Akter" data-id="element-2614">Selina Akter (GD-003)</option>
                  <option value="Habibur Rahman" data-id="element-2615">
                    Habibur Rahman (GD-004)
                  </option>
                </select>
              </div>
              <div data-id="element-2616">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2617">
                  Vessel Type *
                </label>
                <select value={applyForm.vesselType} onChange={e => setApplyForm({
            ...applyForm,
            vesselType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2618">
                  <option value="" data-id="element-2619">Select Type</option>
                  <option value="Boat" data-id="element-2620">Boat</option>
                  <option value="Launch" data-id="element-2621">Launch</option>
                  <option value="Ship" data-id="element-2622">Ship</option>
                  <option value="Trawler" data-id="element-2623">Trawler</option>
                </select>
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-2624">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2625">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2626">
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2627">
                  <div data-id="element-2628">
                    <span className="text-gray-600" data-id="element-2629">Name:</span>{' '}
                    <span className="font-medium" data-id="element-2630">
                      {applyForm.contactName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2631">
                    <span className="text-gray-600" data-id="element-2632">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-2633">
                      {applyForm.contactPhone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2634">
                    <span className="text-gray-600" data-id="element-2635">Email:</span>{' '}
                    <span className="font-medium" data-id="element-2636">
                      {applyForm.contactEmail || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2637">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2638">
                  Tour Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2639">
                  <div data-id="element-2640">
                    <span className="text-gray-600" data-id="element-2641">Type:</span>{' '}
                    <span className="font-medium" data-id="element-2642">
                      {applyForm.tourType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2643">
                    <span className="text-gray-600" data-id="element-2644">Duration:</span>{' '}
                    <span className="font-medium" data-id="element-2645">
                      {applyForm.duration ? `${applyForm.duration} days` : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2646">
                    <span className="text-gray-600" data-id="element-2647">Zone:</span>{' '}
                    <span className="font-medium" data-id="element-2648">
                      {applyForm.forestZone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2649">
                    <span className="text-gray-600" data-id="element-2650">Group Size:</span>{' '}
                    <span className="font-medium" data-id="element-2651">
                      {applyForm.groupSize ? `${applyForm.groupSize} persons` : 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2652">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2653">
                  Guide & Vessel
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2654">
                  <div data-id="element-2655">
                    <span className="text-gray-600" data-id="element-2656">Guide:</span>{' '}
                    <span className="font-medium" data-id="element-2657">
                      {applyForm.guideSelection || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2658">
                    <span className="text-gray-600" data-id="element-2659">Vessel Type:</span>{' '}
                    <span className="font-medium" data-id="element-2660">
                      {applyForm.vesselType || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'tracking' && <div className="space-y-4" data-id="element-2661">
          <div className="flex justify-end" data-id="element-2662">
            <button onClick={() => setActiveTab('startTrip')} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2663">
              <PlayIcon size={16} data-id="element-2664" />
              Start New Trip
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2665">
            {activeTrips.map((trip, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2666">
                <div className="flex items-center justify-between mb-3" data-id="element-2667">
                  <h4 className="font-semibold text-gray-900" data-id="element-2668">
                    {trip.contactName}
                  </h4>
                  <div className="flex items-center gap-1" data-id="element-2669">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-2670"></div>
                    <span className="text-xs text-green-600 font-medium" data-id="element-2671">
                      In Forest
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600" data-id="element-2672">
                  <div data-id="element-2673">
                    <span className="font-medium" data-id="element-2674">Guide:</span> {trip.guideName}
                  </div>
                  <div data-id="element-2675">
                    <span className="font-medium" data-id="element-2676">Vessel Type:</span>{' '}
                    {trip.vesselType}
                  </div>
                  <div data-id="element-2677">
                    <span className="font-medium" data-id="element-2678">Zone:</span> {trip.zone}
                  </div>
                  <div data-id="element-2679">
                    <span className="font-medium" data-id="element-2680">Entry:</span> {trip.entryTime}
                  </div>
                  <div data-id="element-2681">
                    <span className="font-medium" data-id="element-2682">Elapsed:</span> {trip.elapsed}
                  </div>
                </div>
                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-2683">
                  <StopCircleIcon size={16} data-id="element-2684" />
                  End Trip
                </button>
              </div>)}
          </div>
        </div>}

      {activeTab === 'startTrip' && <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-2685">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4" data-id="element-2686">
            <div data-id="element-2687">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2688">
                Start Tour Trip
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2689">
                Select an approved permit and provide trip details
              </p>
            </div>
            <div className="text-right" data-id="element-2690">
              <div className="text-sm text-gray-500" data-id="element-2691">
                Trip ID (Auto-generated)
              </div>
              <div className="font-mono font-bold text-lg text-[#1B5E20]" data-id="element-2692">
                TRP-TOUR-
                {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
              </div>
            </div>
          </div>

          <form onSubmit={e => {
        e.preventDefault();
        alert('Trip started successfully! Status updated to Ongoing.');
        setActiveTab('tracking');
      }} className="space-y-6" data-id="element-2693">
            {/* Select Approved Permit */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-2694">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-2695">
                Select Approved Permit *
              </h3>
              <div data-id="element-2696">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2697">
                  Permit
                </label>
                <select required value={startTripForm.permitId} onChange={e => setStartTripForm({
              ...startTripForm,
              permitId: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2698">
                  <option value="" data-id="element-2699">Select an approved permit</option>
                  {approvedPermits.map(p => <option key={p.permitId} value={p.permitId} data-id="element-2700">
                      {p.permitId} — {p.contactName} ({p.tourType})
                    </option>)}
                </select>
              </div>

              {/* Permit Details Card */}
              {selectedPermit && <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-2701">
                  <div className="flex items-center justify-between" data-id="element-2702">
                    <h4 className="font-semibold text-gray-900" data-id="element-2703">
                      Permit Details
                    </h4>
                    <StatusBadge status={selectedPermit.status} data-id="element-2704" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2705">
                    <div data-id="element-2706">
                      <span className="text-gray-600" data-id="element-2707">Contact:</span>{' '}
                      <span className="font-medium" data-id="element-2708">
                        {selectedPermit.contactName}
                      </span>
                    </div>
                    <div data-id="element-2709">
                      <span className="text-gray-600" data-id="element-2710">Type:</span>{' '}
                      <span className="font-medium" data-id="element-2711">
                        {selectedPermit.tourType}
                      </span>
                    </div>
                    <div data-id="element-2712">
                      <span className="text-gray-600" data-id="element-2713">Duration:</span>{' '}
                      <span className="font-medium" data-id="element-2714">
                        {selectedPermit.duration}
                      </span>
                    </div>
                    <div data-id="element-2715">
                      <span className="text-gray-600" data-id="element-2716">Guide:</span>{' '}
                      <span className="font-medium" data-id="element-2717">
                        {selectedPermit.guideName}
                      </span>
                    </div>
                    <div data-id="element-2718">
                      <span className="text-gray-600" data-id="element-2719">Applied Date:</span>{' '}
                      <span className="font-medium" data-id="element-2720">
                        {selectedPermit.appliedDate}
                      </span>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Journey Details */}
            {selectedPermit && <div className="space-y-4" data-id="element-2721">
                <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-2722">
                  Journey Details
                </h3>
                <div data-id="element-2723">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2724">
                    Start Location (Station) *
                  </label>
                  <select required value={startTripForm.startLocation} onChange={e => setStartTripForm({
              ...startTripForm,
              startLocation: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2725">
                    <option value="" data-id="element-2726">Select Station</option>
                    <option value="chandpai" data-id="element-2727">Chandpai Station</option>
                    <option value="katka" data-id="element-2728">Katka Station</option>
                    <option value="dhangmari" data-id="element-2729">Dhangmari Station</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2730">
                  <div data-id="element-2731">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2732">
                      Start Date *
                    </label>
                    <input type="date" required value={startTripForm.startDate} onChange={e => setStartTripForm({
                ...startTripForm,
                startDate: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2733" />
                  </div>
                  <div data-id="element-2734">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2735">
                      Start Time *
                    </label>
                    <input type="time" required value={startTripForm.startTime} onChange={e => setStartTripForm({
                ...startTripForm,
                startTime: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2736" />
                  </div>
                </div>
              </div>}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-2737">
              <button type="button" onClick={() => setActiveTab('tracking')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" data-id="element-2738">
                Cancel
              </button>
              <button type="submit" disabled={!selectedPermit} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2739">
                <PlayIcon size={18} data-id="element-2740" />
                Start Trip
              </button>
            </div>
          </form>
        </div>}

      {activeTab === 'history' && <DataTable columns={historyColumns} data={tripHistory} onDataChange={setTripHistory} searchPlaceholder="Search trip history..." data-id="element-2741" />}
    </div>;
}